/**
 * DI-3-F — the three upload-session operations of `inspection-3/v1` (2.3.2;
 * DI-1-6 built the server side):
 *
 *   POST …/data-acquisitions/{id}/upload-sessions            → 201 UploadSession
 *        ONE prefix SAS per session (`sasUrls[0]`: `{prefix, url,
 *        expireDateMs}`), container-scoped `cw`, 6 h TTL, `blockSize` 1 MiB;
 *   POST …/upload-sessions/{sessionId}/complete {files[]}     → 202 {jobId}
 *        the manifest; a re-complete of the same manifest is the same job
 *        while its run is not over or after it COMPLETED (BullMQ dedup — the
 *        poll finds that run), a new job after it ended FAILED (2.3.2);
 *   GET  …/upload-sessions/{sessionId}                         → UploadSessionStatus
 *        `OPEN · INGESTING · COMPLETED · FAILED`, `processed / total`,
 *        per-file `errors[]`, `dataAcquisitionState`.
 *
 * M5-NODE-01 (inspection-3/v1 2.6.0) — the open carries the escape hatch's
 * `reason` (1–2000 characters) when the pilot uploads a withheld set under an
 * `optional` version ("Upload images…"); refusals: 409 `upload_blocked` (the
 * version says `blocked` — open and complete), 400 `reason_required` (a
 * withheld set under `optional` without a valid reason).
 */
import type { Inspection3Components } from "@aeriosiq/types";
import { inspection3Fetch } from "./inspection3Fetch.js";

type S = Inspection3Components["schemas"];

export type UploadSession = S["UploadSession"];
export type SasTarget = S["SasTarget"];
export type UploadManifestEntry = Pick<
  S["UploadFileManifestEntry"],
  "filename" | "sizeBytes" | "md5" | "capturedAtMs"
>;
/**
 * DI-3-S75 — a `*_pos.txt` sent with the session (inspection-3/v1
 * `auxFiles[]`): stored at `data-acquisitions/<da>/aux/<relativeFolder>/<filename>`.
 */
export type AuxManifestEntry = S["AuxFileManifestEntry"];
export type IngestJobAccepted = S["IngestJobAccepted"];
export type UploadSessionStatus = S["UploadSessionStatus"];
export type IngestError = S["IngestError"];

function base(daId: string): string {
  return `/data-acquisitions/${encodeURIComponent(daId)}/upload-sessions`;
}

/** The open's body: the sizing hints, and the escape hatch's reason. */
export type OpenUploadSessionBody = Pick<
  S["OpenUploadSessionRequest"],
  "reason"
> & {
  expectedFileCount: number;
  expectedTotalBytes: number;
  /**
   * DI-3-S75 (inspection-3/v1 2.7.0) — `aux` = the pos-only session sent
   * before metadata-complete (`upload/posSession.ts`); absent = images.
   */
  purpose?: "aux";
};

/** The two M5-NODE-01 refusals of the upload-session routes. */
export const UPLOAD_BLOCKED = "upload_blocked";
export const REASON_REQUIRED = "reason_required";

export function openUploadSession(
  daId: string,
  hint: OpenUploadSessionBody,
): Promise<UploadSession> {
  return inspection3Fetch<UploadSession>(base(daId), {
    method: "POST",
    body: hint,
  });
}

/**
 * DI-3-S75 — `auxFiles` (a folder's `*_pos.txt`) only when there is one: a
 * folder without one completes exactly the body it did before (no key).
 */
export function completeUploadSession(
  daId: string,
  sessionId: string,
  files: readonly UploadManifestEntry[],
  auxFiles: readonly AuxManifestEntry[] = [],
): Promise<IngestJobAccepted> {
  return inspection3Fetch<IngestJobAccepted>(
    `${base(daId)}/${encodeURIComponent(sessionId)}/complete`,
    {
      method: "POST",
      body: auxFiles.length > 0 ? { files, auxFiles } : { files },
    },
  );
}

export function getUploadSession(
  daId: string,
  sessionId: string,
): Promise<UploadSessionStatus> {
  return inspection3Fetch<UploadSessionStatus>(
    `${base(daId)}/${encodeURIComponent(sessionId)}`,
  );
}
